import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';
import { Button, Table, Spinner, StatusSelect } from '../../components/ds';
import { useToast } from '../../components/ds';
import './AdsList.css';

const STATUS_DISPLAY = [
  { value: 'draft', label: '草稿' },
  { value: 'active', label: '執行中' },
  { value: 'paused', label: '已暫停' },
  { value: 'archived', label: '封存' },
];

const FILTERS = ['all', 'draft', 'active', 'paused', 'archived'];
const FILTER_LABELS = { all: 'All', draft: 'Draft', active: 'Active', paused: 'Paused', archived: 'Archived' };

export default function AdsList() {
  const [ads, setAds] = useState([]);
  const [summary, setSummary] = useState({});
  const [ptMap, setPtMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();
  const { addToast } = useToast();
  const companyID = localStorage.getItem('portal_company_id');

  useEffect(() => {
    if (!companyID) { navigate('/login'); return; }
    fetchAds();
  }, [companyID, navigate]);

  const fetchAds = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.getPortalAds(companyID);
      setAds(data);
      // Fetch placement types for display name mapping
      try {
        const types = await api.getPortalPlacementTypes();
        const map = {};
        (types || []).forEach((t) => { map[t.name] = t.display_name ? `${t.display_name}` : t.name; });
        setPtMap(map);
      } catch {
        // Optional, don't block
      }
      // Fetch today's summary for active ads
      try {
        const summaryData = await api.getAdsSummary(companyID);
        const map = {};
        (summaryData || []).forEach((s) => { map[String(s.ad_id)] = s; });
        setSummary(map);
      } catch {
        // Summary is optional, don't block
      }
    } catch (err) {
      setError(err.message || 'Failed to load ads');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (adId) => {
    try {
      const result = await api.activatePortalAd(adId);
      if (result.errors && result.errors.length > 0) {
        addToast('部分資源啟用失敗：' + result.errors.join('; '), 'error');
      } else {
        addToast('廣告已啟用！', 'success');
      }
      fetchAds();
    } catch (err) {
      addToast('啟用失敗：' + (err.message || '未知錯誤'), 'error');
    }
  };

  const handlePause = async (adId) => {
    try {
      await api.pausePortalAd(adId);
      addToast('廣告已暫停', 'success');
      fetchAds();
    } catch (err) {
      addToast('暫停失敗：' + err.message, 'error');
    }
  };

  const handleResume = async (adId) => {
    try {
      await api.resumePortalAd(adId);
      addToast('廣告已恢復投放', 'success');
      fetchAds();
    } catch (err) {
      addToast('恢復失敗：' + err.message, 'error');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('zh-TW', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    });
  };

  const getCreativeCount = (ad) => {
    const count = ad.creative_uuid ? 1 : 0;
    return <span style={{ whiteSpace: 'nowrap' }}>{count} 個</span>;
  };

  const getActionButtons = (row) => {
    const buttons = [];

    // View + Edit always available
    buttons.push(
      <Button key="view" variant="ghost" size="sm"
        onClick={(e) => { e.stopPropagation(); navigate(`/ads/${row.id}`); }}>查看</Button>
    );
    buttons.push(
      <Button key="edit" variant="ghost" size="sm"
        onClick={(e) => { e.stopPropagation(); navigate(`/ads/create?edit=${row.id}&step=${row.current_step || 1}`); }}>編輯</Button>
    );

    // Creative actions
    if (!row.creative_uuid) {
      buttons.push(
        <Button key="create-cr" variant="ghost" size="sm"
          onClick={(e) => { e.stopPropagation(); navigate(`/ads/${row.id}/creative`); }}>建立素材</Button>
      );
    }

    // Status actions based on state machine: draft → active ↔ paused
    if (row.status === 'draft' && row.creative_uuid) {
      buttons.push(
        <Button key="activate" size="sm"
          onClick={(e) => { e.stopPropagation(); handleActivate(row.id); }}>啟用</Button>
      );
    }
    if (row.status === 'active') {
      buttons.push(
        <Button key="pause" variant="secondary" size="sm"
          onClick={(e) => { e.stopPropagation(); handlePause(row.id); }}>暫停</Button>
      );
    }
    if (row.status === 'paused') {
      buttons.push(
        <Button key="resume" size="sm"
          onClick={(e) => { e.stopPropagation(); handleResume(row.id); }}>恢復</Button>
      );
    }

    return buttons;
  };

  const filteredAds = filter === 'all'
    ? ads.filter((ad) => ad.status !== 'archived')
    : ads.filter((ad) => ad.status === filter);

  if (loading) {
    return <div className="loading"><Spinner /></div>;
  }

  const totalAds = ads.filter(a => a.status !== 'archived').length;
  const activeAds = ads.filter(a => a.status === 'active').length;

  return (
    <div className="ads-page">
      {/* Stats Row */}
      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-card__label">Total Ads</div>
          <div className="stat-card__value">{totalAds}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__label">Active</div>
          <div className="stat-card__value">{activeAds}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__label">Total Impressions</div>
          <div className="stat-card__value">—</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__label">Daily Budget Used</div>
          <div className="stat-card__value">—</div>
        </div>
      </div>

      {/* Page Header */}
      <div className="page-header">
        <h1>我的廣告</h1>
        <Button onClick={() => navigate('/ads/create')}>+ 建立廣告</Button>
      </div>

      {/* Filter Chips */}
      <div className="filter-bar">
        {FILTERS.map((f) => (
          <button
            key={f}
            className={`filter-chip ${filter === f ? 'active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {FILTER_LABELS[f]}
          </button>
        ))}
      </div>

      {error && <div className="error-box">{error}</div>}

      {!error && filteredAds.length === 0 && (
        <div className="empty-state">
          <p>還沒有廣告</p>
          <p className="empty-state__hint">點擊上方「建立廣告」按鈕建立第一個廣告</p>
        </div>
      )}

      {!error && filteredAds.length > 0 && (
        <Table
          columns={[
            { key: 'name', title: '廣告名稱', width: '14%' },
            { key: 'status', title: '狀態', width: '9%', render: (val) => (
              <StatusSelect value={val} options={STATUS_DISPLAY} disabled />
            )},
            { key: 'creative', title: '素材', width: '6%', render: (_, row) => getCreativeCount(row) },
            { key: 'placement_type', title: '版位', width: '6%', render: (val) => ptMap[val] || val || '—' },
            { key: 'today_requests', title: '今日請求', width: '7%', align: 'right',
              render: (_, row) => {
                const s = summary[String(row.id)];
                return row.status === 'active' && s ? s.requests.toLocaleString() : '—';
              }},
            { key: 'today_impressions', title: '今日曝光', width: '7%', align: 'right',
              render: (_, row) => {
                const s = summary[String(row.id)];
                return row.status === 'active' && s ? s.impressions.toLocaleString() : '—';
              }},
            { key: 'today_clicks', title: '今日點擊', width: '6%', align: 'right',
              render: (_, row) => {
                const s = summary[String(row.id)];
                return row.status === 'active' && s ? s.clicks.toLocaleString() : '—';
              }},
            { key: 'today_ctr', title: 'CTR', width: '5%', align: 'right',
              render: (_, row) => {
                const s = summary[String(row.id)];
                return row.status === 'active' && s ? (s.click_rate * 100).toFixed(1) + '%' : '—';
              }},
            { key: 'today_viewability', title: '可視率', width: '5%', align: 'right',
              render: (_, row) => {
                const s = summary[String(row.id)];
                return row.status === 'active' && s ? s.viewability.toFixed(1) + '%' : '—';
              }},
            { key: 'date_range', title: '走期', width: '12%',
              render: (_, row) => row.start_time && row.end_time
                ? `${formatDate(row.start_time)} ~ ${formatDate(row.end_time)}` : '—' },
            { key: 'actions', title: '', width: '17%', render: (_, row) => (
              <div style={{ display: 'flex', gap: 6 }}>
                {getActionButtons(row)}
              </div>
            )},
          ]}
          data={filteredAds}
          rowKey="id"
          onRowClick={(row) => navigate(`/ads/${row.id}`)}
          emptyText="No ads found"
        />
      )}
    </div>
  );
}
